import { ReactElement, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { distanceXY } from '@geometry'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { v4 } from 'uuid'
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter'
import {
  Alert,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  amber,
  blue,
  cyan,
  deepPurple,
  green,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  teal,
} from '@mui/material/colors'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { CoordinatePointInput, Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useBlockScene, useResultsInvalidation } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { getModel } from '@queries'
import { updateOpening as updateOpeningRequest } from '@mutations'
import { errors } from 'src/constants'
import { buildErrorMessage } from 'src/constants/errors'
import { getContrastTextColor } from 'src/utils/color'
import { alignOpeningsSchema } from './alignSchema'
import AlignSceneMarkers from './components/AlignSceneMarkers'

interface Props {
  resetElements: () => void
  selectedElements: string[]
  /**
   * Called when a row's "Koordinaten übernehmen" action runs, so the caller can move that opening to
   * the front of the selection (making it the reference). Lets the selection source stay with the
   * caller (Structural Planning query params vs. the IFC importer's store).
   */
  onUseOpeningAsReference?: (openingGuid: string) => void
}

interface OpeningWithWall {
  opening: Opening
  wall: ShapeObject
  indexInWall: number
}

interface AlignFormState {
  globalStart: { x: number; y: number; z: number }
  globalEnd: { x: number; y: number; z: number }
}

type OpeningOrientation = 'x' | 'y' | 'mixed'

const openingColors = [
  pink[500],
  blue[500],
  green[500],
  purple[500],
  lime[500],
  deepPurple[500],
  cyan[500],
  amber[500],
  teal[500],
  orange[500],
  lightGreen[500],
]

const lengthNumberFormatter = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 4,
})

const lengthFormatter = (length: number): string => `${lengthNumberFormatter.format(length)}m`

const MultiEditOpenings = ({
  resetElements,
  selectedElements,
  onUseOpeningAsReference,
}: Props): ReactElement => {
  const { projectId } = useParams<{ projectId: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const { walls } = useModelStore(state => state.model)

  const openingsWithWalls = useMemo((): OpeningWithWall[] => {
    return selectedElements
      .map(guid => {
        const wall = find(walls, w => w.openings?.some((o: Opening) => o.guid === guid)) as
          | ShapeObject
          | undefined
        const opening = wall?.openings ? find(wall.openings, { guid }) : undefined
        if (!wall || !opening) return null
        const indexInWall = wall.openings!.findIndex((o: Opening) => o.guid === guid) + 1
        return { opening, wall, indexInWall }
      })
      .filter((x): x is OpeningWithWall => x != null)
  }, [selectedElements, walls])

  const colorByOpeningGuid = useMemo(() => {
    return openingsWithWalls.reduce<Record<string, string>>((acc, { opening }, idx) => {
      acc[opening.guid] = openingColors[idx] ?? 'grey'
      return acc
    }, {})
  }, [openingsWithWalls])

  const alignDefaultValues = useMemo((): AlignFormState | undefined => {
    const first = openingsWithWalls[0]
    if (!first) return undefined
    const p0 = first.opening.shape.points[0]
    const p2 = first.opening.shape.points[2]
    return {
      globalStart: { x: p0.x, y: p0.y, z: p0.z },
      globalEnd: { x: p2.x, y: p2.y, z: p2.z },
    }
  }, [openingsWithWalls])

  const openingOrientationValidation = useMemo(() => {
    if (openingsWithWalls.length === 0) return { orientation: 'mixed' as OpeningOrientation }

    const orientations = openingsWithWalls.map(({ opening }) => {
      const start = opening.shape.points[0]
      const end = opening.shape.points[2]

      if (start.y === end.y) return 'x'
      if (start.x === end.x) return 'y'
      return 'mixed'
    })

    const uniqueOrientations = Array.from(new Set(orientations))

    if (uniqueOrientations.length !== 1 || uniqueOrientations[0] === 'mixed') {
      return {
        orientation: 'mixed' as OpeningOrientation,
        warning: 'Öffnungen sind nicht gleich orientiert',
      }
    }

    const orientation = uniqueOrientations[0] as Exclude<OpeningOrientation, 'mixed'>
    const firstOpening = openingsWithWalls[0].opening
    const firstStart = firstOpening.shape.points[0]
    const referenceCoordinate = orientation === 'x' ? firstStart.y : firstStart.x
    const areInSamePlane = openingsWithWalls.every(({ opening }) => {
      const start = opening.shape.points[0]
      const end = opening.shape.points[2]
      return orientation === 'x'
        ? start.y === referenceCoordinate && end.y === referenceCoordinate
        : start.x === referenceCoordinate && end.x === referenceCoordinate
    })

    return {
      orientation,
      info: areInSamePlane ? undefined : 'Öffnungen sind nicht in der gleichen Ebene',
    }
  }, [openingsWithWalls])

  const buildOpeningFromAlignState = useCallback(
    (state: AlignFormState, opening: Opening): Opening => {
      const p0 = opening.shape.points[0]
      const p2 = opening.shape.points[2]
      const derivedStart = new ImmutableVector3(state.globalStart.x, state.globalStart.y, p0.z)
      const derivedEnd = new ImmutableVector3(state.globalEnd.x, state.globalEnd.y, p2.z)
      const p1 = new ImmutableVector3(derivedEnd.x, derivedEnd.y, derivedStart.z)
      const p3 = new ImmutableVector3(derivedStart.x, derivedStart.y, derivedEnd.z)
      return {
        ...opening,
        shape: {
          points: [derivedStart, p1, derivedEnd, p3],
          guid: v4(),
        },
      }
    },
    [],
  )

  const { mutateAsync: updateOpeningsAsync, isLoading: isAlignSubmitting } = useMutation(
    async (state: AlignFormState) => {
      // Sequential: opening PATCH is read-modify-write; parallel requests can race.
      const results: Awaited<ReturnType<typeof updateOpeningRequest.request>>[] = []
      for (const { opening, wall } of openingsWithWalls) {
        const newOpening = buildOpeningFromAlignState(state, opening)
        results.push(
          await updateOpeningRequest.request(projectId as string, {
            element_guid: wall.guid,
            ...newOpening,
          }),
        )
      }
      return results
    },
    {
      onSuccess: async () => {
        resetElements()
        await invalidateModel(projectId as string)
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        enqueueSnackbar('Öffnungen ausgerichtet und gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        if (errors[error.response?.data?.code as keyof typeof errors]) {
          enqueueSnackbar(errors[error.response?.data?.code as keyof typeof errors], {
            variant: 'error',
          })
          return
        }
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ausrichten der Öffnungen'), {
          variant: 'error',
        })
      },
    },
  )

  useBlockScene(isAlignSubmitting)

  const handleAlignSubmit = useCallback(
    (state: AlignFormState) => {
      updateOpeningsAsync(state)
    },
    [updateOpeningsAsync],
  )

  if (openingsWithWalls.length === 0) {
    return (
      <Stack direction="column" spacing={1} p={2}>
        <Typography variant="body2" color="text.secondary">
          Keine Öffnungen ausgewählt.
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack direction="column" spacing={1}>
      {alignDefaultValues && (
        <Form
          key="align-openings"
          defaultValues={alignDefaultValues}
          validationSchema={alignOpeningsSchema}
          onSubmit={data => handleAlignSubmit(data as AlignFormState)}
        >
          {methods => (
            <Stack direction="column" spacing={1}>
              {openingOrientationValidation.warning && (
                <Alert severity="warning">{openingOrientationValidation.warning}</Alert>
              )}
              {openingOrientationValidation.info && (
                <Alert severity="info">{openingOrientationValidation.info}</Alert>
              )}
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableCell-root': {
                      px: 0.75,
                      py: 0.5,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>
                        x<sub>Start</sub>
                      </TableCell>
                      <TableCell>
                        y<sub>Start</sub>
                      </TableCell>
                      <TableCell>
                        x<sub>Ende</sub>
                      </TableCell>
                      <TableCell>
                        y<sub>Ende</sub>
                      </TableCell>
                      <TableCell>Länge</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const referenceOpening = openingsWithWalls[0]?.opening
                      const referenceStart = referenceOpening?.shape.points[0]
                      const referenceEnd = referenceOpening?.shape.points[2]

                      return openingsWithWalls.map(({ opening }, index) => {
                        const openingStart = opening.shape.points[0]
                        const openingEnd = opening.shape.points[2]
                        const markerColor = colorByOpeningGuid[opening.guid] ?? 'grey'
                        const markerTextColor = getContrastTextColor(markerColor)
                        const indexBase1 = index + 1
                        const xStartDiffers =
                          typeof referenceStart?.x === 'number' &&
                          openingStart.x !== referenceStart.x
                        const yStartDiffers =
                          typeof referenceStart?.y === 'number' &&
                          openingStart.y !== referenceStart.y
                        const xEndDiffers =
                          typeof referenceEnd?.x === 'number' && openingEnd.x !== referenceEnd.x
                        const yEndDiffers =
                          typeof referenceEnd?.y === 'number' && openingEnd.y !== referenceEnd.y
                        const openingLength = distanceXY(openingStart, openingEnd)

                        return (
                          <TableRow key={opening.guid}>
                            <TableCell
                              sx={{
                                bgcolor: markerColor,
                                color: markerTextColor,
                                fontWeight: 600,
                                textAlign: 'center',
                              }}
                            >
                              {indexBase1}
                            </TableCell>
                            <TableCell
                              sx={xStartDiffers ? { bgcolor: 'warning.light' } : undefined}
                            >
                              {openingStart.x}
                            </TableCell>
                            <TableCell
                              sx={yStartDiffers ? { bgcolor: 'warning.light' } : undefined}
                            >
                              {openingStart.y}
                            </TableCell>
                            <TableCell sx={xEndDiffers ? { bgcolor: 'warning.light' } : undefined}>
                              {openingEnd.x}
                            </TableCell>
                            <TableCell sx={yEndDiffers ? { bgcolor: 'warning.light' } : undefined}>
                              {openingEnd.y}
                            </TableCell>
                            <TableCell>{lengthFormatter(openingLength)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Koordinaten übernehmen">
                                <IconButton
                                  type="button"
                                  size="small"
                                  onClick={() => {
                                    const p0 = opening.shape.points[0]
                                    const p2 = opening.shape.points[2]
                                    methods.setValue(
                                      'globalStart',
                                      { x: p0.x, y: p0.y, z: p0.z },
                                      { shouldDirty: true },
                                    )
                                    methods.setValue(
                                      'globalEnd',
                                      { x: p2.x, y: p2.y, z: p2.z },
                                      { shouldDirty: true },
                                    )

                                    onUseOpeningAsReference?.(opening.guid)
                                  }}
                                  data-cy={`multi-edit-openings-use-coords-${opening.guid}`}
                                >
                                  <AlignHorizontalCenterIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="column" spacing={1}>
                <Divider>Gemeinsamer Start- und Endpunkt (XY)</Divider>
                <AlignSceneMarkers
                  openingsWithWalls={openingsWithWalls}
                  colorByOpeningGuid={colorByOpeningGuid}
                />
                <CoordinatePointInput
                  label="Start"
                  name="globalStart"
                  pointManipulation={{ z: { disabled: true, hidden: true } }}
                />
                <CoordinatePointInput
                  label="Ende"
                  name="globalEnd"
                  pointManipulation={{ z: { disabled: true, hidden: true } }}
                />
                <Typography>
                  {(() => {
                    const globalStart = methods.watch('globalStart')
                    const globalEnd = methods.watch('globalEnd')
                    const globalStartPoint = new ImmutableVector3(
                      globalStart?.x ?? 0,
                      globalStart?.y ?? 0,
                      globalStart?.z ?? 0,
                    )
                    const globalEndPoint = new ImmutableVector3(
                      globalEnd?.x ?? 0,
                      globalEnd?.y ?? 0,
                      globalEnd?.z ?? 0,
                    )
                    const newLength = distanceXY(globalStartPoint, globalEndPoint)

                    return `Neue Länge: ${lengthFormatter(newLength)}`
                  })()}
                </Typography>
                <SaveButton
                  type="submit"
                  loading={isAlignSubmitting}
                  fullWidth
                  data-cy="multi-edit-openings-align-submit"
                >
                  Ausrichten und speichern
                </SaveButton>
              </Stack>
            </Stack>
          )}
        </Form>
      )}
    </Stack>
  )
}

export default MultiEditOpenings
